import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Download,
  Eye,
  X,
  RefreshCw,
  Trash2,
  Globe,
  CheckCircle,
  AlertTriangle,
  Clock,
  XCircle,
  Lock,
  ExternalLink,
} from "lucide-react";

import BaseModal from "../../../components/BaseModal/BaseModal";
import StatusBadge, {
  StatusVariant,
} from "../../../components/StatusBadge/StatusBadge";
import Button from "../../../components/Button/Button";
import DataTable, {
  Column,
} from "../../../components/DataTable/DataTable";
import Pagination from "../../../components/Pagination/Pagination";

import styles from "./DomainProvisioning.module.css";
import axios, { AxiosError } from "axios";
import toast from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const API_GET = `${BASE_URL}/domain/get`;
const API_STATUS = `${BASE_URL}/domain/status`;
const API_DELETE = `${BASE_URL}/domain/delete`;

interface DomainRecord {
  id: string;
  tenant: string;
  email: string;
  domain: string;
  dnsStatus: "Verified" | "Pending" | "Failed";
  provisioningStatus:
    | "Active"
    | "Provisioning"
    | "Failed"
    | "Pending"
    | "Suspended";
  sslStatus: "Active" | "Pending" | "Failed" | "Expired" | "None";
  sslExpiry: string;
  nginxConfigured: boolean;
  created: string;
}

interface ApiError {
  message: string;
}

function dnsVariant(s: DomainRecord["dnsStatus"]): StatusVariant {
  return s === "Verified" ? "active" : s === "Pending" ? "trial" : "error";
}
function provVariant(
  s: DomainRecord["provisioningStatus"]
): StatusVariant {
  const m: Record<string, StatusVariant> = {
    Active: "active",
    Provisioning: "trial",
    Failed: "error",
    Pending: "trial",
    Suspended: "suspended",
  };
  return m[s] || "info";
}
function sslVariant(s: DomainRecord["sslStatus"]): StatusVariant {
  const m: Record<string, StatusVariant> = {
    Active: "active",
    Pending: "trial",
    Failed: "error",
    Expired: "warning",
    None: "suspended",
  };
  return m[s] || "info";
}

const DomainDetailDrawer: React.FC<{
  domain: DomainRecord | null;
  onClose: () => void;
}> = ({ domain: d, onClose }) => {
  if (!d) return null;
  return (
    <>
      <div className={styles.drawerBackdrop} onClick={onClose} />
      <aside className={styles.drawer} aria-label="Domain details">
        <div className={styles.drawerHeader}>
          <div>
            <h3 className={styles.drawerName}>{d.domain}</h3>
            <span className={styles.drawerId}>
              {d.tenant} · {d.id}
            </span>
          </div>
          <button
            className={styles.drawerClose}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>

        <div className={styles.drawerBody}>
          <div className={styles.drawerStats}>
            <div className={styles.drawerStatItem}>
              <span className={styles.drawerStatLabel}>DNS</span>
              <StatusBadge
                variant={dnsVariant(d.dnsStatus)}
                size="sm"
              />
            </div>
            <div className={styles.drawerStatItem}>
              <span className={styles.drawerStatLabel}>SSL</span>
              <StatusBadge
                variant={sslVariant(d.sslStatus)}
                size="sm"
              />
            </div>
            <div className={styles.drawerStatItem}>
              <span className={styles.drawerStatLabel}>Status</span>
              <StatusBadge
                variant={provVariant(d.provisioningStatus)}
                size="sm"
              />
            </div>
          </div>

          <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Domain Info</h4>
            {[
              { label: "Domain", value: d.domain },
              { label: "Tenant", value: d.tenant },
              { label: "Email", value: d.email },
              {
                label: "Nginx",
                value: d.nginxConfigured
                  ? "Configured"
                  : "Not Configured",
              },
              { label: "SSL Expiry", value: d.sslExpiry || "—" },
              { label: "Created", value: d.created },
            ].map((r) => (
              <div key={r.label} className={styles.drawerRow}>
                <span className={styles.drawerRowLabel}>
                  {r.label}
                </span>
                <span className={styles.drawerRowValue}>
                  {r.value}
                </span>
              </div>
            ))}
          </section>
          
           <section className={styles.drawerSection}>
            <h4 className={styles.drawerSectionTitle}>Actions</h4>
            <div className={styles.drawerActions}>
              {d.provisioningStatus === 'Failed' && <Button variant="primary" size="sm" icon={<RefreshCw size={14} />}>Retry Provisioning</Button>}
              {d.sslStatus === 'Expired' && <Button variant="secondary" size="sm" icon={<Lock size={14} />}>Renew SSL</Button>}
              {d.provisioningStatus === 'Active' && <Button variant="ghost" size="sm" icon={<ExternalLink size={14} />}>Visit Domain</Button>}
              <Button variant="primary" size="sm" icon={<ExternalLink size={14} />}>Retry Provisioning</Button>
              <Button variant="secondary" size="sm" icon={<ExternalLink size={14} />}>Renew SSL</Button>
              <Button variant="ghost" size="sm" icon={<ExternalLink size={14} />}>Visit Domain</Button>
              <Button variant="danger" size="sm" icon={<Trash2 size={14} />}>Remove Domain</Button>
            </div>
          </section>
        </div>
      </aside>
    </>
  );
};

const RemoveDomainModal: React.FC<{
  domain: DomainRecord | null;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ domain: d, onClose, onConfirm }) => (
  <BaseModal
    isOpen={!!d}
    onClose={onClose}
    title="Remove Domain"
    subtitle={d?.domain}
    size="sm"
    footer={
      <>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          size="sm"
          icon={<Trash2 size={14} />}
          onClick={onConfirm}
        >
          Remove
        </Button>
      </>
    }
  >
    {d && (
      <div className={styles.confirmBody}>
        <div className={styles.confirmIcon}>
          <Trash2 size={24} />
        </div>
        <p className={styles.confirmText}>
          Remove <strong>{d.domain}</strong> from{" "}
          <strong>{d.tenant}</strong>? This will delete the domain
          record.
        </p>
        <div className={styles.confirmMeta}>
          <span>{d.id}</span>
          <span>{d.provisioningStatus}</span>
        </div>
      </div>
    )}
  </BaseModal>
);

const DomainProvisioning: React.FC = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sslFilter, setSslFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [detailDomain, setDetailDomain] =
    useState<DomainRecord | null>(null);
  const [removeTarget, setRemoveTarget] =
    useState<DomainRecord | null>(null);
  const [domains, setDomains] = useState<DomainRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const PER_PAGE = 8;

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_GET);
      const rows = res.data.data || [];

      const mapped: DomainRecord[] = rows.map((d: any) => {
        const rawDns = (d.dns_status || "Pending").trim();
        const rawProv = (d.provisioning_status || "Pending").trim();
        const rawSsl = (d.ssl_status || "None").trim();
        const rawNginx = (d.nginx_configured || "").toString().trim();

        const normDns =
          rawDns.toLowerCase() === "trial" ? "Pending" : rawDns;
        const normProv =
          rawProv.toLowerCase() === "trial" ? "Pending" : rawProv;
        const normSsl =
          rawSsl.toLowerCase() === "trial" ? "Pending" : rawSsl;

        return {
          id: String(d.id),
          tenant: d.tenant,
          email: d.email,
          domain: d.domain,
          dnsStatus: normDns.replace(
            /^\w/,
            (c: string) => c.toUpperCase()
          ) as DomainRecord["dnsStatus"],
          provisioningStatus: normProv.replace(
            /^\w/,
            (c: string) => c.toUpperCase()
          ) as DomainRecord["provisioningStatus"],
          sslStatus: normSsl.replace(
            /^\w/,
            (c: string) => c.toUpperCase()
          ) as DomainRecord["sslStatus"],
          sslExpiry: "—",
          nginxConfigured:
            rawNginx.toLowerCase() === "configured" ||
            rawNginx.toLowerCase() === "yes" ||
            rawNginx === "1" ||
            rawNginx.toLowerCase() === "ok" ||
            d.nginx_configured === true,
          created: d.created_at
            ? new Date(d.created_at).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "",
        };
      });

      setDomains(mapped);
    } catch (err) {
      const error = err as AxiosError<ApiError>;
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to load domains";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const stats = useMemo(
    () => ({
      total: domains.length,
      active: domains.filter(
        (d) => d.provisioningStatus === "Active"
      ).length,
      failed: domains.filter(
        (d) => d.provisioningStatus === "Failed"
      ).length,
      provisioning: domains.filter(
        (d) =>
          d.provisioningStatus === "Provisioning" ||
          d.provisioningStatus === "Pending"
      ).length,
      sslActive: domains.filter(
        (d) => d.sslStatus === "Active"
      ).length,
      sslExpired: domains.filter(
        (d) => d.sslStatus === "Expired"
      ).length,
      sslFailed: domains.filter(
        (d) => d.sslStatus === "Failed"
      ).length,
    }),
    [domains]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return domains.filter(
      (d) =>
        (!q ||
          d.domain.toLowerCase().includes(q) ||
          d.tenant.toLowerCase().includes(q) ||
          d.id.toLowerCase().includes(q)) &&
        (statusFilter === "All" ||
          d.provisioningStatus === statusFilter) &&
        (sslFilter === "All" || d.sslStatus === sslFilter)
    );
  }, [search, statusFilter, sslFilter, domains]);

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("All");
    setSslFilter("All");
    setPage(1);
  };

  const hasFilters =
    search || statusFilter !== "All" || sslFilter !== "All";

  const handleRemoveDomain = async () => {
    if (!removeTarget) return;
    try {
      await axios.delete(`${API_DELETE}/${removeTarget.id}`);
      toast.success("Domain removed");
      setRemoveTarget(null);
      fetchDomains();
    } catch (err) {
      const error = err as AxiosError<ApiError>;
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to remove domain";
      toast.error(msg);
    }
  };

  const handleRetryProvision = async (d: DomainRecord) => {
    if (d.provisioningStatus === "Active") {
      toast.success("Domain is already provisioned");
      return;
    }

    try {
      await axios.put(`${API_STATUS}/${d.id}`, {
        dns_status: "Verified",
        provisioning_status: "Active",
        ssl_status: "Active",
        nginx_configured: "Ok",
      });
      toast.success("Provisioning restarted");
      await fetchDomains();
    } catch (err) {
      const error = err as AxiosError<ApiError>;
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update status";
      toast.error(msg);
    }
  };

  const columns: Column<DomainRecord>[] = [
    {
      key: "domain",
      header: "DOMAIN",
      render: (d) => (
        <div className={styles.domainCell}>
          <div className={styles.domainAvatar}>
            <Globe size={14} />
          </div>
          <div>
            <div className={styles.domainName}>{d.domain}</div>
            <div className={styles.domainTenant}>{d.tenant}</div>
          </div>
        </div>
      ),
    },
    {
      key: "dns",
      header: "DNS",
      render: (d) => (
        <StatusBadge variant={dnsVariant(d.dnsStatus)} size="sm" />
      ),
    },
    {
      key: "provisioning",
      header: "PROVISIONING",
      render: (d) => (
        <StatusBadge
          variant={provVariant(d.provisioningStatus)}
          size="sm"
        />
      ),
    },
    {
      key: "ssl",
      header: "SSL",
      render: (d) => (
        <div className={styles.sslCell}>
          <StatusBadge
            variant={sslVariant(d.sslStatus)}
            size="sm"
          />
          {/* {d.sslStatus === "Active" && (
            <span className={styles.sslExpiry}>exp {d.sslExpiry}</span>
          )} */}
        </div>
      ),
    },
    {
      key: "nginx",
      header: "NGINX",
      render: (d) => (
        <span
          className={`${styles.nginxChip} ${
            d.nginxConfigured ? styles.nginxOk : styles.nginxNo
          }`}
        >
          {d.nginxConfigured ? (
            <CheckCircle size={11} />
          ) : (
            <XCircle size={11} />
          )}
          {d.nginxConfigured ? "OK" : "No"}
        </span>
      ),
    },
    {
      key: "created",
      header: "CREATED",
      render: (d) => (
        <span className={styles.dateText}>{d.created}</span>
      ),
    },
    {
      key: "actions",
      header: "ACTIONS",
      render: (d) => (
        <div
          className={`${styles.actionBtns} actionBtns`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={styles.actionBtn}
            title="View details"
            onClick={() => setDetailDomain(d)}
          >
            <Eye size={13} />
          </button>

          <button
            className={styles.actionBtn}
            title="Retry provisioning"
            onClick={() => handleRetryProvision(d)}
          >
            <RefreshCw size={13} />
          </button>

          <button
            className={`${styles.actionBtn} ${styles.actionDanger}`}
            title="Remove"
            onClick={() => setRemoveTarget(d)}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Domain Provisioning</h1>
          <p className={styles.pageSubtitle}>
            Monitor automated domain onboarding and SSL certificates
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="secondary"
            size="md"
            icon={<Download size={14} />}
          >
            Export
          </Button>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Total Domains</span>
            <span className={styles.statIcon}>
              <Globe size={15} />
            </span>
          </div>
          <div className={styles.statValue}>{stats.total}</div>
          <div className={styles.statSub}>{stats.active} active</div>
        </div>
        <div
          className={`${styles.statCard} ${styles.statCardAccent}`}
        >
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Active Domains</span>
            <span
              className={`${styles.statIcon} ${styles.statIconAccent}`}
            >
              <CheckCircle size={15} />
            </span>
          </div>
          <div
            className={`${styles.statValue} ${styles.statValueAccent}`}
          >
            {stats.active}
          </div>
          <div className={styles.statSub}>Fully provisioned</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Provisioning</span>
            <span className={styles.statIcon}>
              <Clock size={15} />
            </span>
          </div>
          <div className={styles.statValue}>
            {stats.provisioning}
          </div>
          <div className={styles.statSub}>In progress</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>Failed</span>
            <span className={styles.statIcon}>
              <AlertTriangle size={15} />
            </span>
          </div>
          <div className={styles.statValue}>{stats.failed}</div>
          <div className={styles.statSub}>Needs attention</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statTop}>
            <span className={styles.statLabel}>SSL Active</span>
            <span className={styles.statIcon}>
              <Lock size={15} />
            </span>
          </div>
          <div className={styles.statValue}>
            {stats.sslActive}
          </div>
          <div className={styles.statSub}>
            {stats.sslExpired} expired, {stats.sslFailed} failed
          </div>
        </div>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <Search size={14} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search domains or tenants…"
            />
            {search && (
              <button
                className={styles.searchClear}
                onClick={() => setSearch("")}
              >
                <X size={12} />
              </button>
            )}
          </div>

          <div className={styles.filterPills}>
            {(
              ["All", "Active", "Provisioning", "Failed", "Pending"] as const
            ).map((s) => (
              <button
                key={s}
                className={`${styles.pill} ${
                  statusFilter === s ? styles.pillActive : ""
                }`}
                onClick={() => {
                  setStatusFilter(s);
                  setPage(1);
                }}
              >
                {s}
              </button>
            ))}
          </div>

          <div className={styles.selects}>
            <select
              className={styles.select}
              value={sslFilter}
              onChange={(e) => {
                setSslFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="All">All SSL</option>
              <option value="Active">SSL Active</option>
              <option value="Pending">SSL Pending</option>
              <option value="Failed">SSL Failed</option>
              <option value="Expired">SSL Expired</option>
              <option value="None">No SSL</option>
            </select>
          </div>
        </div>

        <div className={styles.resultsBar}>
          <span className={styles.resultsText}>
            Showing <strong>{paged.length}</strong> of{" "}
            <strong>{filtered.length}</strong> domains
          </span>
          {hasFilters && (
            <button
              className={styles.clearFilters}
              onClick={clearFilters}
            >
              <X size={10} /> Clear filters
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={paged}
          keyExtractor={(d) => d.id}
          onRowClick={setDetailDomain}
          emptyState={
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>No domains found</p>
              <p className={styles.emptySub}>
                Try adjusting your search or filters
              </p>
              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          }
        />

        <Pagination
          page={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          perPage={PER_PAGE}
          onPageChange={(p) => setPage(p)}
        />
      </div>

      <DomainDetailDrawer
        domain={detailDomain}
        onClose={() => setDetailDomain(null)}
      />
      <RemoveDomainModal
        domain={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveDomain}
      />
    </div>
  );
};

export default DomainProvisioning;